/**
 * Phase 6-γ.2 — Marketing Cockpit page.
 *
 * Renders the single-call `/api/v1/crm/revenue/cockpit-summary` response:
 *   - 6 KPI tiles with delta-vs-previous-period
 *   - 5-stage lifecycle funnel (horizontal)
 *   - Per-campaign table
 *   - By-platform + by-region bar cards
 *
 * Period selector controls all panels via shared `since`/`until` state.
 * Trailing-window comparison (default last 30 days vs the 30 days before that)
 * — see ADR-0032 amendment for rationale.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LineChart, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api } from "../../lib/api";
import { useT, useLocale } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { PerformanceFreshnessChip }
  from "../../components/app/PerformanceFreshnessChip";
import { Badge } from "../../components/ui/badge";
import PeriodSelector, { rangeForPreset } from "../../components/app/PeriodSelector";
import CockpitSuggestions from "../../components/app/CockpitSuggestions";
import BenchmarksPanel from "../../components/app/BenchmarksPanel";
import OnboardingBanner from "../../components/app/OnboardingBanner";
import LandingPageCockpitTiles from "../../components/app/LandingPageCockpitTiles";

const PRESETS = [
  { key: "7d", days: 7 },
  { key: "30d", days: 30 },
  { key: "90d", days: 90 },
  { key: "365d", days: 365 },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

function fmtEur(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR",
                                              maximumFractionDigits: 0 }).format(n);
}

function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${(n * 100).toFixed(0)}%`;
}

function delta(curr, prev) {
  if (prev === null || prev === undefined || prev === 0) return null;
  if (curr === null || curr === undefined) return null;
  return (curr - prev) / prev;
}

function DeltaBadge({ value, testid }) {
  if (value === null || value === undefined) {
    return <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid={testid}>
      <Minus className="h-3 w-3" /> —
    </span>;
  }
  const up = value >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const cls = up ? "text-emerald-600" : "text-rose-600";
  return <span className={`text-xs inline-flex items-center gap-1 ${cls}`} data-testid={testid}>
    <Icon className="h-3 w-3" />
    {up ? "+" : ""}{(value * 100).toFixed(1)}%
  </span>;
}

function fmtRoas(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(2)}×`;
}

export function BenchmarkLine({ benchmark, value, fmt, t, metric }) {
  if (!benchmark || benchmark.median === undefined
        || benchmark.median === null) return null;
  const median = benchmark.median;
  const fmtV = fmt ? fmt(median) : median;
  // For cost metrics (CPL/CPC/CPM), LOWER is BETTER — invert the
  // threshold check so "below industry p25" means "exceptionally
  // cheap acquisition" rather than "starving the channel".
  const lowerIsBetter = metric === "cpl_eur"
    || metric === "cpc_eur" || metric === "cpm_eur";
  let chip = null;
  if (typeof value === "number"
        && benchmark.p75 !== undefined && benchmark.p75 !== null
        && benchmark.p25 !== undefined && benchmark.p25 !== null) {
    const beatsP75 = lowerIsBetter
      ? value < benchmark.p25 : value > benchmark.p75;
    const trailsP25 = lowerIsBetter
      ? value > benchmark.p75 : value < benchmark.p25 && value > 0;
    if (beatsP75) {
      chip = (
        <span data-testid="industry-chip-top-quartile"
                className="ml-1 inline-flex items-center text-[10px]
                                rounded-md bg-emerald-100 text-emerald-800
                                px-1 py-0 font-medium">
          {t("cockpit.industry_top_quartile", "Top Quartil")}
        </span>
      );
    } else if (trailsP25) {
      chip = (
        <span data-testid="industry-chip-below-industry"
                className="ml-1 inline-flex items-center text-[10px]
                                rounded-md bg-amber-100 text-amber-800
                                px-1 py-0 font-medium">
          {t("cockpit.industry_below", "Unter Branche")}
        </span>
      );
    }
  }
  return (
    <div
      data-testid="industry-median-line"
      className="mt-0.5 text-[10px] text-muted-foreground tabular-nums
                      inline-flex items-center"
      title={(benchmark.source_reference || benchmark.source || "")}
    >
      <span>{t("cockpit.industry_label", "Branche")}: {fmtV}</span>
      {chip}
    </div>
  );
}

function KpiTile({ label, value, prev, fmt, testid, tooltip,
                              benchmark, metric, t }) {
  const d = delta(value, prev);
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground" title={tooltip || ""}>
          {label}
        </div>
        <div className="mt-2 text-2xl font-semibold tabular-nums" data-testid={`${testid}-value`}>
          {fmt ? fmt(value) : (value ?? "—")}
        </div>
        <div className="mt-1">
          <DeltaBadge value={d} testid={`${testid}-delta`} />
        </div>
        {benchmark && t && (
          <BenchmarkLine benchmark={benchmark} value={value}
                                fmt={fmt} t={t} />
        )}
      </CardContent>
    </Card>
  );
}

function FunnelRow({ stage, count, prevCount, label, max }) {
  const conv = prevCount && prevCount > 0 ? count / prevCount : null;
  const widthPct = max > 0 ? Math.max(2, (count / max) * 100) : 2;
  return (
    <div className="space-y-1" data-testid={`funnel-stage-${stage}`}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums">
          <span data-testid={`funnel-count-${stage}`}>{count}</span>
          {conv !== null && (
            <span className="ml-2 text-xs text-muted-foreground"
                    data-testid={`funnel-conv-${stage}`}>
              ({fmtPct(conv)})
            </span>
          )}
        </span>
      </div>
      <div className="h-3 rounded bg-muted/60 overflow-hidden">
        <div className="h-full bg-primary/80 transition-all duration-300"
                style={{ width: `${widthPct}%` }} />
      </div>
    </div>
  );
}

function BarRow({ label, value, max, sub }) {
  const pct = max > 0 ? Math.max(1, (value / max) * 100) : 1;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-sm">
        <span className="truncate max-w-[60%]" title={label}>{label}</span>
        <span className="tabular-nums">{fmtEur(value)}</span>
      </div>
      <div className="h-2 rounded bg-muted/60 overflow-hidden">
        <div className="h-full bg-primary/70" style={{ width: `${pct}%` }} />
      </div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

// Master-P3 Wave 6.4 — polls `/api/v1/ad-platforms/performance/runs`
// and surfaces the latest run's mode + timestamp as a chip.
function PerformanceFreshnessRow() {
  const { data } = useQuery({
    queryKey: ["performance", "runs", "latest"],
    queryFn: async () => {
      const r = await api.get(
        "/ad-platforms/performance/runs?limit=1");
      return r.data;
    },
    refetchInterval: 60000,
    retry: false,
  });
  const latest = (data && data.items && data.items[0]) || null;
  if (!latest) return null;
  return (
    <div className="flex items-center gap-2 text-xs
                    text-muted-foreground"
         data-testid="perf-freshness-row">
      <span>Werbedaten:</span>
      <PerformanceFreshnessChip
        testId="cockpit-perf-freshness-chip"
        data={{
          data_source: latest.mode === "real_pull" ? "real"
                                                    : "synthetic",
          is_synthetic: latest.mode !== "real_pull",
          imported_at: latest.completed_at,
          environment: (latest.mode === "real_pull"
                        ? "production" : "preview"),
        }} />
      <span data-testid="perf-freshness-platform">
        {latest.platform}
      </span>
      <span>·</span>
      <span>{latest.records_imported} neue Zeilen</span>
      {latest.records_superseded > 0 && (
        <>
          <span>·</span>
          <span data-testid="perf-freshness-superseded">
            {latest.records_superseded} retro-korrigiert
          </span>
        </>
      )}
    </div>
  );
}



export default function Cockpit() {
  const t = useT();
  const { locale } = useLocale();
  const [period, setPeriod] = useState(() => ({
    mode: "preset", preset: "30d", ...rangeForPreset("30d"),
  }));

  const { data, isLoading, error } = useQuery({
    queryKey: ["cockpit-summary", period.since, period.until, null],
    queryFn: async () => {
      const r = await api.get("/crm/revenue/cockpit-summary", {
        params: { since: period.since, until: period.until },
      });
      return r.data;
    },
  });

  // Phase 8 Slice 3 — industry benchmarks (aggregate-only).
  const { data: benchmarksData } = useQuery({
    queryKey: ["cockpit-benchmarks"],
    queryFn: async () => {
      const r = await api.get("/cockpit/benchmarks");
      return r.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const benchmarkFor = useMemo(() => {
    const rows = benchmarksData?.rows || [];
    return (metric, platform = "all", campaignKind = "all") => {
      const score = (r) => {
        let s = 0;
        if (r.platform === platform) s += 4;
        else if (r.platform === "all") s += 1;
        if (r.campaign_kind === campaignKind) s += 2;
        else if (r.campaign_kind === "all") s += 1;
        return s;
      };
      const matching = rows
        .filter((r) => r.metric === metric)
        .sort((a, b) => score(b) - score(a));
      if (!matching.length) return null;
      const top = matching[0];
      const sameBucket = matching.filter(
        (r) => r.platform === top.platform
          && r.campaign_kind === top.campaign_kind);
      const out = { source: top.source,
                          source_reference: top.source_reference,
                          platform_used: top.platform };
      for (const r of sameBucket) out[r.aggregate_kind] = r.value;
      return out;
    };
  }, [benchmarksData]);

  const stageLabels = useMemo(() => ({
    LeadCreated: t("cockpit.funnel_lead_created", "Leads"),
    LeadQualified: t("cockpit.funnel_qualified", "Qualified"),
    AppointmentBooked: t("cockpit.funnel_appointment", "Appointments"),
    OrderCreated: t("cockpit.funnel_order", "Orders"),
    SaleCompleted: t("cockpit.funnel_sale", "Sales"),
  }), [t]);

  if (isLoading && !data) {
    return (
      <div className="p-6" data-testid="cockpit-loading">
        <div className="text-sm text-muted-foreground">{t("cockpit.loading", "Lade Marktintelligenz…")}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-rose-600 text-sm" data-testid="cockpit-error">
          {t("cockpit.error", "Fehler beim Laden des Cockpits")}: {String(error?.message || "request_failed")}
        </div>
      </div>
    );
  }

  const totals = data?.totals || {};
  const prev = data?.previous_period_totals || {};
  const funnel = data?.funnel || [];
  const byCampaign = data?.by_campaign || [];
  const byPlatform = data?.by_platform || [];
  const byTemplateArchetype = data?.by_template_archetype || [];
  const byRegion = data?.by_region || [];
  const maxFunnel = Math.max(1, ...funnel.map((s) => s.count || 0));
  const maxPlatform = Math.max(1, ...byPlatform.map((p) => p.revenue_eur || 0));
  const maxRegion = Math.max(1, ...byRegion.map((r) => r.revenue_eur || 0));

  return (
    <div className="p-6 space-y-8" data-testid="cockpit-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight inline-flex items-center gap-2">
            <LineChart className="h-7 w-7" />
            {t("cockpit.title", "Marktintelligenz")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("cockpit.subtitle", "Revenue-Attribution und Funnel-Performance.")}
          </p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} locale={locale} />
      </header>

      <OnboardingBanner />

      {/* Master-P3 Wave 6.4 — Freshness + mode chip. Pulls the most
          recent ad_import_runs row to expose whether the KPI strip
          below is backed by a real fetch or seeded synthetic data. */}
      <PerformanceFreshnessRow />

      {/* KPI strip */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3"
                   data-testid="kpi-strip">
        <KpiTile label={t("cockpit.kpi_revenue", "Umsatz")}
                    value={totals.revenue_eur} prev={prev.revenue_eur}
                    fmt={fmtEur} testid="kpi-revenue" />
        <KpiTile label={t("cockpit.kpi_profit", "Bruttogewinn")}
                    value={totals.gross_profit_eur} prev={prev.gross_profit_eur}
                    fmt={fmtEur} testid="kpi-profit" />
        <KpiTile label={t("cockpit.kpi_ad_spend", "Werbeausgaben")}
                    value={totals.total_ad_spend_eur}
                    prev={prev.total_ad_spend_eur}
                    fmt={fmtEur} testid="kpi-ad-spend"
                    tooltip={t("cockpit.ad_spend_tooltip",
                                  "Aggregiert aus Werbeausgaben-Events im Zeitraum.")} />
        <KpiTile label="ROAS"
                    value={totals.roas} prev={prev.roas}
                    fmt={fmtRoas}
                    testid="kpi-roas"
                    benchmark={benchmarkFor("roas", "all", "lead")}
                    metric="roas"
                    t={t}
                    tooltip={totals.total_ad_spend_eur > 0
                      ? t("cockpit.roas_tooltip_active",
                            "Gesamte Werbeausgabe: {{spend}} im Zeitraum.")
                          .replace("{{spend}}", fmtEur(totals.total_ad_spend_eur))
                      : t("cockpit.roas_tooltip_empty",
                            "Keine Werbeausgaben im Zeitraum. CSV hochladen oder Eintrag erfassen.")} />
        <KpiTile label="CPL"
                    value={totals.cpl_eur} prev={prev.cpl_eur}
                    fmt={fmtEur} testid="kpi-cpl"
                    benchmark={benchmarkFor("cpl_eur", "all", "lead")}
                    metric="cpl_eur"
                    t={t}
                    tooltip={totals.total_ad_spend_eur > 0
                      ? t("cockpit.cpl_tooltip_active",
                            "Werbeausgaben {{spend}} / {{leads}} Leads.")
                          .replace("{{spend}}", fmtEur(totals.total_ad_spend_eur))
                          .replace("{{leads}}", String(totals.lead_count || 0))
                      : t("cockpit.cpl_tooltip_empty",
                            "Keine Werbeausgaben im Zeitraum.")} />
        <KpiTile label={t("cockpit.kpi_leads", "Leads")}
                    value={totals.lead_count} prev={prev.lead_count}
                    testid="kpi-leads" />
        <KpiTile label={t("cockpit.kpi_sales", "Verkäufe")}
                    value={totals.sale_count} prev={prev.sale_count}
                    testid="kpi-sales" />
      </section>

      <LandingPageCockpitTiles />

      {/* Funnel */}
      <Card data-testid="cockpit-funnel">
        <CardHeader>
          <CardTitle>{t("cockpit.funnel_title", "Lifecycle-Funnel")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {funnel.map((s, i) => (
            <FunnelRow key={s.stage} stage={s.stage}
                          count={s.count}
                          prevCount={i === 0 ? null : funnel[i - 1].count}
                          label={stageLabels[s.stage] || s.stage}
                          max={maxFunnel} />
          ))}
        </CardContent>
      </Card>

      {/* Phase 7 Slice E — Suggested next-action card */}
      <CockpitSuggestions since={period.since} until={period.until} />

      <BenchmarksPanel />

      {/* By-campaign table */}
      <Card data-testid="cockpit-by-campaign">
        <CardHeader>
          <CardTitle>{t("cockpit.by_campaign_title", "Pro Kampagne")}</CardTitle>
        </CardHeader>
        <CardContent>
          {byCampaign.length === 0 ? (
            <div className="text-sm text-muted-foreground"
                    data-testid="by-campaign-empty">
              {t("cockpit.no_data", "Keine Daten im gewählten Zeitraum.")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="campaign-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-muted-foreground border-b">
                    <th className="py-2 pr-3">{t("cockpit.col_campaign", "Kampagne")}</th>
                    <th className="py-2 pr-3 text-right">{t("cockpit.col_leads", "Leads")}</th>
                    <th className="py-2 pr-3 text-right">{t("cockpit.col_sales", "Sales")}</th>
                    <th className="py-2 pr-3 text-right">{t("cockpit.col_revenue", "Umsatz")}</th>
                    <th className="py-2 pr-3 text-right">{t("cockpit.col_spend", "Ausgabe")}</th>
                    <th className="py-2 pr-3 text-right">ROAS</th>
                    <th className="py-2 text-right">CPL</th>
                  </tr>
                </thead>
                <tbody>
                  {byCampaign.map((c) => (
                    <tr key={c.campaign_id}
                         className="border-b last:border-0 hover:bg-muted/40 cursor-pointer"
                         data-testid={`campaign-row-${c.campaign_id}`}
                         onClick={() => { window.location.href = `/app/cockpit/campaigns/${c.campaign_id}`; }}>
                      <td className="py-2 pr-3 font-medium truncate max-w-[280px]">
                        <Link to={`/app/cockpit/campaigns/${c.campaign_id}`}
                              className="hover:underline"
                              data-testid={`campaign-link-${c.campaign_id}`}
                              onClick={(e) => e.stopPropagation()}>
                          {c.campaign_name}
                        </Link>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{c.lead_count}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{c.sale_count}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtEur(c.revenue_eur)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums"
                            data-testid={`campaign-spend-${c.campaign_id}`}>
                        {fmtEur(c.total_ad_spend_eur)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums"
                            data-testid={`campaign-roas-${c.campaign_id}`}>
                        {fmtRoas(c.roas)}
                      </td>
                      <td className="py-2 text-right tabular-nums"
                            data-testid={`campaign-cpl-${c.campaign_id}`}>
                        {fmtEur(c.cpl_eur)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Platform + Region */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Phase 9 Slice 4 — by_template_archetype rollup. */}
        <Card data-testid="cockpit-by-template-archetype"
              className="lg:col-span-2">
          <CardHeader>
            <CardTitle>
              {t("cockpit.by_template_archetype_title", "Nach Vorlage")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {byTemplateArchetype.length === 0 ? (
              <div className="text-sm text-muted-foreground"
                    data-testid="cockpit-by-archetype-empty">
                {t("cockpit.by_template_archetype_empty",
                     "Noch keine Kampagne aus einer Vorlage erstellt.")}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="py-1 pr-3">Vorlage</th>
                      <th className="py-1 pr-3 text-right">Kampagnen</th>
                      <th className="py-1 pr-3 text-right">Umsatz</th>
                      <th className="py-1 pr-3 text-right">Spend</th>
                      <th className="py-1 pr-3 text-right">ROAS</th>
                      <th className="py-1 pr-3 text-right">CPL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byTemplateArchetype.map((a) => (
                      <tr key={a.archetype_key} className="border-t"
                            data-testid={`cockpit-archetype-${a.archetype_key}`}>
                        <td className="py-1.5 pr-3 font-mono text-xs">
                          {a.archetype_key}
                        </td>
                        <td className="py-1.5 pr-3 text-right">
                          {a.campaign_count}
                        </td>
                        <td className="py-1.5 pr-3 text-right">
                          {fmtEur(a.total_revenue_eur)}
                        </td>
                        <td className="py-1.5 pr-3 text-right">
                          {fmtEur(a.total_ad_spend_eur)}
                        </td>
                        <td className="py-1.5 pr-3 text-right">
                          {a.roas !== null && a.roas !== undefined
                            ? fmtRoas(a.roas) : "—"}
                        </td>
                        <td className="py-1.5 pr-3 text-right">
                          {a.cpl_eur !== null && a.cpl_eur !== undefined
                            ? fmtEur(a.cpl_eur) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="cockpit-by-platform">
          <CardHeader>
            <CardTitle>{t("cockpit.by_platform_title", "Nach Plattform")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {byPlatform.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {t("cockpit.no_data", "Keine Daten im gewählten Zeitraum.")}
              </div>
            ) : byPlatform.map((p) => (
              <BarRow key={p.platform} label={p.platform}
                          value={p.revenue_eur} max={maxPlatform}
                          sub={
                            <span data-testid={`platform-meta-${p.platform}`}>
                              {`${p.lead_count} leads · ${p.sale_count} sales · `}
                              {t("cockpit.col_spend", "Ausgabe")}{": "}
                              {fmtEur(p.total_ad_spend_eur)}
                              {p.roas !== null && p.roas !== undefined
                                ? ` · ROAS ${fmtRoas(p.roas)}` : ""}
                            </span>
                          } />
            ))}
          </CardContent>
        </Card>
        <Card data-testid="cockpit-by-region">
          <CardHeader>
            <CardTitle>{t("cockpit.by_region_title", "Nach Region")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {byRegion.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {t("cockpit.no_data", "Keine Daten im gewählten Zeitraum.")}
              </div>
            ) : byRegion.map((r) => (
              <BarRow key={r.region} label={r.region}
                          value={r.revenue_eur} max={maxRegion}
                          sub={`${r.lead_count} leads · ${r.sale_count} sales`} />
            ))}
          </CardContent>
        </Card>
      </section>

      {/* Footer link */}
      <p className="text-xs text-muted-foreground" data-testid="cockpit-footnote">
        {t("cockpit.footnote",
              "Daten kombinieren Revenue-Events (CRM) und Werbeausgaben-Events. CSV oder API zum Synchronisieren nutzen.")}
        {" "}
        <Link to="/app/ad-spend" className="underline mr-3"
                 data-testid="cockpit-go-to-ad-spend">
          {t("cockpit.go_to_ad_spend", "Werbeausgaben verwalten")}
        </Link>
        <Link to="/app/integrations" className="underline">
          {t("cockpit.go_to_integrations", "Integrationen verwalten")}
        </Link>
      </p>
    </div>
  );
}
